import React from "react";
import { ArrowRight, ShieldCheck, Network, Cpu, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { stats as statIcons } from "../../data/mock";
import { useLanguage } from "../../i18n/LanguageContext";

export default function Hero() {
  const { t } = useLanguage();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const statKeys = ["projects", "years", "satisfaction", "support"];

  return (
    <section id="hero" className="hero-bg relative overflow-hidden pt-32 pb-24 lg:pt-40 lg:pb-32">
      <div className="absolute inset-0 stars" />
      <div className="absolute inset-0 grid-overlay" />

      <div className="orb bg-sky-500/40 w-[520px] h-[520px] top-[-120px] left-[-120px]" />
      <div className="orb bg-cyan-400/30 w-[420px] h-[420px] top-[120px] right-[-80px]" />

      <div className="pointer-events-none absolute inset-0">
        <ShieldCheck className="floaty absolute w-12 h-12 text-sky-400/25 top-[18%] left-[8%]" strokeWidth={1.2} />
        <Network className="floaty delay-1 absolute w-14 h-14 text-cyan-400/25 bottom-[22%] right-[10%]" strokeWidth={1.2} />
        <Cpu className="floaty delay-2 absolute w-10 h-10 text-blue-400/25 bottom-[15%] left-[15%]" strokeWidth={1.2} />
        <Sparkles className="floaty delay-3 absolute w-9 h-9 text-cyan-300/25 top-[24%] right-[18%]" strokeWidth={1.2} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/25 bg-cyan-500/10 mb-8">
            <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
            <span className="text-[11px] tracking-[0.22em] uppercase text-cyan-200 font-semibold">{t("hero.kicker")}</span>
          </div>

          <h1 className="font-display font-bold text-white text-[46px] leading-[1.05] sm:text-6xl lg:text-[76px]">
            {t("hero.title1")}
            <br />
            <span className="grad-text">{t("hero.titleAccent")}</span>
          </h1>

          <p className="mt-8 text-slate-300/90 text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed">
            {t("hero.description")}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button onClick={() => scrollTo("services")} className="btn-primary h-12 px-7 rounded-xl font-semibold text-base border-0">
              {t("hero.cta1")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button onClick={() => scrollTo("contact")} variant="outline" className="btn-ghost h-12 px-7 rounded-xl font-semibold text-base">
              {t("hero.cta2")}
            </Button>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {statIcons.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={statKeys[i]} className="glass lift rounded-2xl p-6 text-center">
                <div className="mx-auto w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500/20 to-cyan-400/20 border border-cyan-400/25 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-cyan-300" strokeWidth={2} />
                </div>
                <div className="font-display text-4xl lg:text-5xl grad-text font-bold tracking-tight">{s.value}</div>
                <div className="mt-1 text-xs uppercase tracking-widest text-slate-400">{t(`hero.stats.${statKeys[i]}`)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
